import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@shopify/polaris';
import {
  HomeIcon,
  ProductIcon,
  QuestionCircleIcon,
  PhoneIcon,
  HeartIcon,
  CartIcon,
  PersonIcon,
  ExitIcon,
  XIcon,
} from '@shopify/polaris-icons';
import { useAuthStore } from '../../store/authStore';
import { useAuth } from '../../hooks/useAuth';
import { useShortlistStore } from '../../store/shortlistStore';
import { useInquiryStore } from '../../store/inquiryStore';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

export function MobileMenu({ open, onClose }: MobileMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const { logout } = useAuth();

  const shortlistCount = useShortlistStore((s) => s.getCount());
  const inquiryBasketCount = useInquiryStore((s) => s.getItemCount());

  const handleNav = useCallback(
    (path: string) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose]
  );

  const handleLogout = useCallback(() => {
    logout();
    onClose();
  }, [logout, onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const isActive = (path: string, startsWith = false) => {
    if (startsWith) return location.pathname.startsWith(path);
    return location.pathname === path;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`mobile-overlay ${open ? 'mobile-overlay--open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu Panel */}
      <aside
        className={`mobile-menu ${open ? 'mobile-menu--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header */}
        <div className="mobile-menu-header">
          <img
            src="/GS_logo_no_tagline.png"
            alt="Gift Sangam"
            style={{ height: '40px', width: 'auto' }}
          />
          <button
            className="mobile-menu-close"
            onClick={onClose}
            aria-label="Close menu"
          >
            <Icon source={XIcon} />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="mobile-menu-nav">
          <div className="mobile-menu-section">
            <MobileNavItem
              icon={HomeIcon}
              label="Home"
              active={isActive('/')}
              onClick={() => handleNav('/')}
            />
            <MobileNavItem
              icon={ProductIcon}
              label="Products"
              active={isActive('/categories', true) || isActive('/products', true)}
              onClick={() => handleNav('/categories')}
            />
            <MobileNavItem
              icon={ProductIcon}
              label="Catalogue"
              active={isActive('/catalogue')}
              onClick={() => handleNav('/catalogue')}
            />
            <MobileNavItem
              icon={QuestionCircleIcon}
              label="About Us"
              active={isActive('/about')}
              onClick={() => handleNav('/about')}
            />
            <MobileNavItem
              icon={QuestionCircleIcon}
              label="FAQ"
              active={isActive('/faq')}
              onClick={() => handleNav('/faq')}
            />
            <MobileNavItem
              icon={PhoneIcon}
              label="Contact Us"
              active={isActive('/contact')}
              onClick={() => handleNav('/contact')}
            />
          </div>

          <div className="mobile-menu-divider" />

          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Your Activity</div>
            <MobileNavItem
              icon={HeartIcon}
              label={`Shortlist${shortlistCount > 0 ? ` (${shortlistCount})` : ''}`}
              active={isActive('/shortlist')}
              onClick={() => handleNav('/shortlist')}
            />
            <MobileNavItem
              icon={CartIcon}
              label={`Inquiry Basket${inquiryBasketCount > 0 ? ` (${inquiryBasketCount})` : ''}`}
              active={isActive('/inquiry-basket')}
              onClick={() => handleNav('/inquiry-basket')}
            />
            {isAuthenticated ? (
              <MobileNavItem
                icon={ProductIcon}
                label="My Inquiries"
                active={isActive('/inquiries')}
                onClick={() => handleNav('/inquiries')}
              />
            ) : (
              <MobileNavItem
                icon={PersonIcon}
                label="Track Inquiry"
                active={isActive('/track-inquiry')}
                onClick={() => handleNav('/track-inquiry')}
              />
            )}
          </div>

          {isAuthenticated && (
            <>
              <div className="mobile-menu-divider" />
              <div className="mobile-menu-section">
                <MobileNavItem
                  icon={ExitIcon}
                  label="Log out"
                  active={false}
                  onClick={handleLogout}
                />
              </div>
            </>
          )}
        </nav>
      </aside>
    </>
  );
}

function MobileNavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: typeof HomeIcon;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`mobile-nav-item ${active ? 'mobile-nav-item--active' : ''}`}
      onClick={onClick}
    >
      <span className="mobile-nav-icon">
        <Icon source={icon} />
      </span>
      <span className="mobile-nav-label">{label}</span>
    </button>
  );
}
